import './assigned-units.scss';
import '@fortawesome/fontawesome-free/css/all.css';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useEffect, useState } from 'react';
import { OrderProduct, OrderReturnStatus, OrderType } from '../../../clients/quipli-service/order/order';
import { ProductInventoryUnit, ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { AssignUnitSelect } from '../assign-unit-select/assign-unit-select';
import { Spinner } from '../../../components/spinner/spinner';
import { SwapUnit } from '../swap-unit/swap-unit';

export const AssignedUnits = ({
  locationId,
  orderId,
  orderProductId,
  setRootAttributes,
}: {
  locationId: number;
  orderId: number;
  orderProductId: number;
  setRootAttributes?: (o: any) => undefined;
}) => {
  const [orderProduct, setOrderProduct] = useState<OrderProduct>();
  const [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>('unitNumber');
  const [serialSaleUnitsEnabled, setSerialSaleUnitsEnabled] = useState(false);
  const [combinedSerialUnitNumber, setCombinedSerialUnitNumber] = useState(false);
  const { getOrderProductById, getMultipleConfigurations } = useAPI();

  async function fetchOrderProduct() {
    setOrderProduct(undefined);
    const op = await getOrderProductById(orderId, orderProductId);
    setOrderProduct(op);
  }

  useEffect(() => {
    fetchOrderProduct();
  }, [orderId, orderProductId]);
  useEffect(() => {
    const fetchUnitTrackingConfig = async () => {
      const configs = await getMultipleConfigurations([
        'CONF_UNIT_TRACKING_VIA',
        'CONF_SALE_SERIAL_TRACKING',
        'CONF_COMBINE_SERIAL_UNIT_NUMBER',
      ]);
      setUnitTrackingField(
        configs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
      );
      setSerialSaleUnitsEnabled(configs.find((c) => c.variable === 'CONF_SALE_SERIAL_TRACKING')?.value === '1');
      setCombinedSerialUnitNumber(configs.find((c) => c.variable === 'CONF_COMBINE_SERIAL_UNIT_NUMBER')?.value === '1');
    };
    fetchUnitTrackingConfig();
  }, []);

  return (
    <div className="assigned-units">
      {orderProduct ? (
        (orderProduct.type === OrderType.Rental || serialSaleUnitsEnabled) &&
        orderProduct.product.trackingType === ProductTrackingType.SerialUnits ? (
          <SerialUnitDisplay
            locationId={locationId}
            orderProduct={orderProduct}
            refresh={fetchOrderProduct}
            setRootAttributes={setRootAttributes}
            unitTrackingField={unitTrackingField}
            combinedSerialUnitNumber={combinedSerialUnitNumber}
          />
        ) : (
          <QuantityUnitDisplay orderProduct={orderProduct} />
        )
      ) : (
        <Spinner />
      )}
    </div>
  );
};

const QuantityUnitDisplay = ({ orderProduct }: { orderProduct: OrderProduct }) => {
  return <span>{orderProduct.quantity} unit(s)</span>;
};

const SerialUnitDisplay = ({
  locationId,
  orderProduct,
  refresh,
  setRootAttributes,
  unitTrackingField,
  combinedSerialUnitNumber,
}: {
  locationId: number;
  orderProduct: OrderProduct;
  refresh: () => void;
  setRootAttributes?: (o: any) => undefined;
  unitTrackingField: 'unitNumber' | 'serialNumber';
  combinedSerialUnitNumber: boolean;
}) => {
  const [assignedUnitIds, setAssignedUnitIds] = useState(orderProduct.assignedUnits.map((u) => u.id));
  const [swap, setSwap] = useState<[OrderProduct, ProductInventoryUnit]>();
  const { assignUnits } = useAPI();

  if (
    (!orderProduct.status || orderProduct.status === OrderReturnStatus.NotStarted) &&
    orderProduct.type !== OrderType.Sale
  ) {
    setRootAttributes && setRootAttributes({ 'data-quantity': orderProduct.quantity });
    return (
      <AssignUnitSelect
        locationId={locationId}
        uniqueId={`${orderProduct.id}`}
        productId={orderProduct.product.id}
        quantity={orderProduct.quantity}
        startDate={orderProduct.rentalStartDate}
        endDate={orderProduct.rentalEndDate}
        selectedUnitIds={assignedUnitIds}
        onChange={async (unitIds) => {
          await assignUnits(orderProduct.orderId, orderProduct.id, unitIds);
          setRootAttributes && setRootAttributes({ assignmentChange: true });
          setAssignedUnitIds(unitIds);
        }}
        setRootAttributes={setRootAttributes}
        orderProductType={orderProduct.type}
      />
    );
  } else {
    return (
      <ul>
        {orderProduct.assignedUnits.length > 0 ? (
          orderProduct?.assignedUnits.map((au) => (
            <AssignedUnit
              key={au.id}
              orderProduct={orderProduct}
              unit={au}
              swapUnit={() => setSwap([orderProduct, au])}
              unitTrackingField={unitTrackingField}
              combinedSerialUnitNumber={combinedSerialUnitNumber}
            />
          ))
        ) : (
          <li>
            <span>N/A</span>
          </li>
        )}
        {swap && (
          <SwapUnit
            orderProduct={swap[0]}
            locationId={locationId}
            inventoryUnit={swap[1]}
            isOpen={swap !== undefined}
            onClose={() => {
              setSwap(undefined);
              refresh();
            }}
          />
        )}
      </ul>
    );
  }
};

const AssignedUnit = ({
  orderProduct,
  unit,
  swapUnit,
  unitTrackingField,
  combinedSerialUnitNumber,
}: {
  orderProduct: OrderProduct;
  unit: ProductInventoryUnit;
  swapUnit: () => void;
  unitTrackingField: 'serialNumber' | 'unitNumber';
  combinedSerialUnitNumber: boolean;
}) => {
  return (
    <li className="assigned-unit">
      {orderProduct.status === OrderReturnStatus.OnRent && (
        <button
          className="vertical-center"
          title="Swap Unit"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            swapUnit();
          }}
        >
          <i className="fa-solid fa-shuffle"></i>
        </button>
      )}
      {combinedSerialUnitNumber ? (
        <>
          <span>Unit - {unit['unitNumber']}</span>
          <span>Serial - {unit['serialNumber']}</span>
        </>
      ) : (
        <span>{unit[unitTrackingField]}</span>
      )}
    </li>
  );
};
